import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import { allNews, getWorkById } from "@/mocks";

const PAGE_SIZE = 6;
const TAGS = ["全部", "新片定档", "幕后特辑", "深度解读", "修复重映", "热播追踪", "跨界改编", "奖项动态", "制作揭秘"];

export default function NewsList() {
  const [tag, setTag] = useState("全部");
  const [page, setPage] = useState(1);

  const filtered = useMemo(
    () => (tag === "全部" ? allNews : allNews.filter((n) => n.tag === tag)),
    [tag],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const headline = filtered[0];
  const rest = paged.filter((n) => n.id !== headline?.id);

  const changeTag = (next: string) => {
    setTag(next);
    setPage(1);
  };

  return (
    <SiteLayout>
      <section className="w-full border-b border-background-200/70 bg-background-100">
        <div className="mx-auto w-full max-w-content px-4 py-6 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视资讯" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">影视资讯</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            追踪「女友的妈妈」相关影视作品的最新动态，涵盖定档消息、幕后特辑、修复重映与剧情深度解读。
          </p>

          <div className="mt-5 flex flex-wrap gap-1.5">
            {TAGS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => changeTag(t)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                  tag === t
                    ? "bg-primary-500 font-medium text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-8 md:py-10">
        <div className="mx-auto grid w-full max-w-content grid-cols-1 gap-8 px-4 md:px-6 lg:grid-cols-12 lg:gap-6">
          <div className="lg:col-span-8">
            {headline ? (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5">
                  {paged.map((article) => (
                    <NewsCard key={article.id} article={article} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setPage(Math.max(1, safePage - 1))}
                      disabled={safePage === 1}
                      aria-label="上一页"
                      className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <i className="ri-arrow-left-s-line text-lg leading-none" />
                    </button>
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setPage(i + 1)}
                        className={`h-9 min-w-9 cursor-pointer rounded-md px-2 text-sm transition-colors ${
                          safePage === i + 1
                            ? "bg-primary-500 font-medium text-background-50"
                            : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setPage(Math.min(totalPages, safePage + 1))}
                      disabled={safePage === totalPages}
                      aria-label="下一页"
                      className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <i className="ri-arrow-right-s-line text-lg leading-none" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-background-200/80 bg-background-100 px-4 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-primary-500">
                  <i className="ri-newspaper-line text-2xl leading-none" />
                </span>
                <h2 className="mt-4 font-heading text-base font-semibold text-foreground-950">暂无新闻资讯</h2>
                <p className="mt-2 text-sm text-foreground-600">该分类下还没有内容，先看看其他栏目的资讯吧。</p>
                <div className="mt-5 flex flex-wrap justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => changeTag("全部")}
                    className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-600"
                  >
                    查看全部资讯
                  </button>
                  <Link
                    to="/"
                    className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 hover:border-primary-300"
                  >
                    返回首页
                  </Link>
                </div>
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200/80 bg-background-100 p-4 md:p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">热门阅读</h2>
              <ul className="mt-2">
                {allNews.slice(0, 5).map((article, i) => (
                  <li key={article.id}>
                    <Link
                      to={`/news/detail/${article.id}`}
                      className="group flex cursor-pointer items-start gap-2.5 border-b border-background-200/70 py-3 last:border-b-0"
                    >
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded font-label text-[11px] font-bold ${
                          i < 3 ? "bg-accent-500 text-background-50" : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="min-w-0">
                        <span className="line-clamp-2 block text-xs leading-relaxed text-foreground-900 transition-colors group-hover:text-primary-700">
                          {article.title}
                        </span>
                        <span className="mt-1 block text-[11px] text-foreground-500">{article.tag} · {article.date}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-4 rounded-lg border border-background-200/80 bg-background-50 p-4 md:p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">相关影视</h2>
              <div className="mt-3 space-y-3">
                {allNews.slice(0, 4).map((article) => {
                  const work = getWorkById(article.relatedWorkId);
                  if (!work) return null;
                  return (
                    <Link
                      key={article.id}
                      to={`/detail/${work.id}`}
                      className="group flex cursor-pointer items-center gap-3"
                    >
                      <span className="h-14 w-10 shrink-0 overflow-hidden rounded bg-background-200">
                        <img
                          src={work.poster}
                          alt={`${work.title} 海报`}
                          title={`${work.title} 剧情介绍`}
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="line-clamp-1 block text-xs font-medium text-foreground-900 group-hover:text-primary-700">
                          {work.title}
                        </span>
                        <span className="mt-1 block text-[11px] text-foreground-500">{work.year} · {work.genres[0]}</span>
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>

            {rest.length > 0 && (
              <div className="mt-4 rounded-lg bg-secondary-50 p-4 md:p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">编辑精选</h2>
                <p className="mt-2 text-xs leading-relaxed text-foreground-700">{rest[0].summary}</p>
                <Link
                  to={`/news/detail/${rest[0].id}`}
                  className="mt-3 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs font-medium text-primary-700"
                >
                  继续阅读
                  <i className="ri-arrow-right-line text-sm leading-none" />
                </Link>
              </div>
            )}
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}